import { useState, useEffect, useRef } from 'react'
import { analysesApi } from '../api'

/**
 * Custom hook for polling analysis status
 * Automatically polls the current step endpoint every 3 seconds
 * Stops polling when analysis reaches terminal state (completed/failed)
 *
 * @param {string} analysisId - Analysis UUID
 * @param {boolean} enabled - Enable/disable polling (default: true)
 * @returns {Object} { status, error, isPolling }
 */
export const useAnalysisStatus = (analysisId, enabled = true) => {
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)
  const [isPolling, setIsPolling] = useState(false)
  const intervalRef = useRef(null)

  useEffect(() => {
    // Don't poll if disabled or no analysis ID
    if (!enabled || !analysisId) {
      return
    }

    const pollStatus = async () => {
      try {
        const response = await analysesApi.getCurrentStep(analysisId)
        setStatus(response.data)
        setIsPolling(true)

        // Stop polling if terminal state reached
        if (
          response.data.analysis_status === 'completed' ||
          response.data.analysis_status === 'failed'
        ) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          setIsPolling(false)
        }
      } catch (err) {
        setError(err)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        setIsPolling(false)
      }
    }

    // Poll immediately on mount/enable
    pollStatus()

    // Then poll every 3 seconds
    intervalRef.current = setInterval(pollStatus, 3000)

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setIsPolling(false)
    }
  }, [analysisId, enabled])

  return { status, error, isPolling }
}
